import React, { useRef, useState, useEffect } from "react";
import Webcam from "react-webcam";
import FlipCameraIosIcon from "@mui/icons-material/FlipCameraIos";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";

interface WebcamCaptureProps {
  onUploadComplete: (data: { chassisNo: string }) => void;
}

const FACING_MODE_USER = "user";
const FACING_MODE_ENVIRONMENT = "environment";

const CaptureChassisNo: React.FC<WebcamCaptureProps> = ({
  onUploadComplete,
}) => {
  const webcamRef = useRef<Webcam>(null);
  const [open, setOpen] = useState(false);
  const [facingMode, setFacingMode] = useState(FACING_MODE_ENVIRONMENT);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(
    null
  );
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const axiosPrivate = useAxiosPrivate();

  const videoConstraints: MediaTrackConstraints = {
    facingMode: facingMode,
  };

  const handleSwitchCamera = () => {
    setFacingMode((prevMode) =>
      prevMode === FACING_MODE_USER ? FACING_MODE_ENVIRONMENT : FACING_MODE_USER
    );
    setError(null);
  };

  const handleOpen = async () => {
    try {
      console.log("Requesting camera permission");
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraPermission(true);
      stream.getTracks().forEach((track) => track.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(
        (device) => device.kind === "videoinput"
      );
      console.log("Enumerated devices:", videoInputs);
      setHasMultipleCameras(videoInputs.length > 1);

      try {
        await navigator.mediaDevices.getUserMedia({
          video: { facingMode: FACING_MODE_ENVIRONMENT },
        });
        setFacingMode(FACING_MODE_ENVIRONMENT);
        console.log("Back camera initialized successfully");
      } catch (backErr) {
        console.error("Back camera failed:", backErr);
        setFacingMode(FACING_MODE_USER);
        setError("Back camera unavailable, switched to front camera.");
        console.log("Switched to front camera due to back camera failure");
      }

      setOpen(true);
      setScanning(true);
      setError(null);
    } catch (err) {
      console.error("Error opening camera:", err);
      setCameraPermission(false);
      setOpen(true);
      setError(
        "Camera access is required. Please enable it in your tablet settings."
      );
    }
  };

  const handleClose = () => {
    if (webcamRef.current?.stream) {
      const tracks = webcamRef.current.stream.getTracks();
      tracks.forEach((track) => track.stop());
    }

    setOpen(false);
    setScanning(false);
    setError(null);
    setCameraPermission(null);
  };

  useEffect(() => {
    if (!scanning || cameraPermission !== true) return;

    let isMounted = true;
    let attemptCount = 0;
    const maxAttempts = 10;

    const interval = setInterval(async () => {
      if (!isMounted || !webcamRef.current?.video) return;

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (context) {
        const video = webcamRef.current.video;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          async (blob) => {
            if (!blob || !isMounted) return;

            const formData = new FormData();
            formData.append("file", blob, "captured-image.jpeg");

            try {
              const response = await axiosPrivate.post(
                "/get_dc_no_from_ocr",
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
              );

              let chassis_no = "";
              if (typeof response.data === "string") {
                chassis_no = response.data.trim();
              } else if (response.data && response.data.chassis_no) {
                chassis_no = response.data.chassis_no.trim();
              }

              if (chassis_no) {
                toast.success(`Chassis Number detected: ${chassis_no}`);
                onUploadComplete({ chassisNo: chassis_no });
                handleClose();
              } else {
                attemptCount++;
                if (attemptCount >= maxAttempts) {
                  setError(
                    "Could not detect chassis number. Adjust the camera or lighting."
                  );
                  setScanning(false);
                }
              }
            } catch (error) {
              console.error("Error calling OCR API:", error);
              attemptCount++;
              if (attemptCount >= maxAttempts) {
                setError("An error occurred. Please try again.");
                setScanning(false);
              }
            }
          },
          "image/jpeg",
          0.8
        );
      }
    }, 2000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [axiosPrivate, onUploadComplete, scanning, cameraPermission]);

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{ color: "#6A1B9A", fontSize: "48px" }}
        aria-label="Open camera to scan chassis number"
      >
        <PhotoCameraIcon fontSize="large" />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="scan-dialog-title"
      >
        <DialogTitle
          id="scan-dialog-title"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            backgroundColor: "#6A1B9A",
            color: "white",
          }}
        >
          Auto Detect Chassis Number
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "20px",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Point the camera at the chassis number and hold the tablet steady
            for 2 seconds.
          </Typography>

          {cameraPermission === true ? (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: "640px",
                border: "2px solid #6A1B9A",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                style={{ width: "100%", height: "auto" }}
                onUserMediaError={(err) => {
                  console.error("Webcam initialization error:", err);
                  setError("Failed to initialize camera, using front camera.");
                  setFacingMode(FACING_MODE_USER);
                }}
              />

              {scanning && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                    background: "rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Box
                    sx={{
                      width: "80%",
                      height: "6px",
                      background: "#6A1B9A",
                      position: "absolute",
                      top: "50%",
                      left: "10%",
                      transform: "translateY(-50%)",
                      animation: "scanning 2s ease-in-out infinite",
                      boxShadow: "0 0 12px rgba(106, 27, 154, 0.8)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "10%",
                      left: "10%",
                      width: "80%",
                      height: "80%",
                      border: "2px dashed #6A1B9A",
                      borderRadius: "8px",
                      opacity: 0.7,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      bottom: "10px",
                      width: "100%",
                      textAlign: "center",
                      color: "white",
                      background: "rgba(0, 0, 0, 0.5)",
                      padding: "5px",
                    }}
                  >
                    Scanning...
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {error || "Checking camera permissions..."}
            </Typography>
          )}

          {error && cameraPermission === true && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 3, mt: 2 }}>
            <IconButton
              onClick={handleSwitchCamera}
              disabled={!hasMultipleCameras || cameraPermission !== true}
              sx={{
                color: "#6A1B9A",
                backgroundColor: "rgba(106, 27, 154, 0.1)",
                "&:hover": { backgroundColor: "rgba(106, 27, 154, 0.2)" },
                padding: "8px",
              }}
              aria-label="Switch camera"
            >
              <FlipCameraIosIcon />
            </IconButton>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "#6A1B9A",
                backgroundColor: "rgba(106, 27, 154, 0.1)",
                "&:hover": { backgroundColor: "rgba(106, 27, 154, 0.2)" },
                padding: "8px",
              }}
              aria-label="Close scanner"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>

      <style>
        {`
          @keyframes scanning {
            0% { top: 0%; opacity: 0.5; }
            50% { top: 95%; opacity: 1; }
            100% { top: 0%; opacity: 0.5; }
          }
        `}
      </style>
    </>
  );
};

export default CaptureChassisNo;
